
import { logger } from "../logger/Logger";
import * as et from "../application/EventTypes";
import { SYNTHETIC_ROOT_ID } from "../globals";

/** @import {Viewer3DImpl} from "../application/Viewer3DImpl" */
/** @import {Model} from "../application/Model" */
/** @import {InstanceTree} from "../wgs/scene/InstanceTree" */

// Optimization: Avoid full traversal of the instance tree if just a few nodes were hidden.
const SMALL_SELECTION_THRESHOLD = 1000;
export class VisibilityManager {

  /** @type {Viewer3DImpl} */
  viewerImpl;

  /** @type {Model} */
  model;

  /**
   * Keeps track of hidden nodes. Only applies when there's no isolated node being tracked.
   *
   * @type {Set<number>}
   */
  hiddenNodes = new Set();

  /**
   * Keeps track of isolated nodes
   *
   * @type {Set<number>}
   */
  isolatedNodes = new Set();

  /**
   * @param {Viewer3DImpl} viewerImpl
   * @param {Model} model
   */
  constructor(viewerImpl, model) {
    this.viewerImpl = viewerImpl;

    //Currently the visibility manager works on a single model only
    //so we make this explicit here.
    this.model = model;
  }

  /**
   * @returns {InstanceTree|null}
   */
  getInstanceTree() {
    if (this.model) return this.model.getInstanceTree();
    // must never be the case, but maybe somewhere in the outter world...
    return null;
  }

  /**
   * @returns {number[]}
   */
  getIsolatedNodes() {
    return Array.from(this.isolatedNodes);
  }

  /**
   * @returns {number[]}
   */
  getHiddenNodes() {
    return Array.from(this.hiddenNodes);
  }

  /**
   * @param {boolean} visible - visible flag applied to all dbIds/fragments
   * @param {boolean} [trackIsolation=false] - optional flag to track isolation, defaults to false
   * @param {number|null} [rootId=null] - optional root ID to track, defaults to null
   */
  setAllVisibility(visible, trackIsolation = false, rootId = null) {

    // Avoid traversing the whole instance tree if the changes are small.

    // Todo: This is just a special-case fix for frequent cases of moderate node count.
    // With more time and changes, we may consider a more general solution by managing
    // visibility in hierarchically accumulated visibility flags in InstanceTree instead. (https://autodesk.atlassian.net/browse/VIZX-3013)

    // Only apply the small change optimization when it's safe:
    // - Don't optimize for 2D models (they need special handling for unselectable objects)
    // - Don't optimize when a specific rootId is provided (could miss visibility updates)
    const smallChange = visible &&
    this.isolatedNodes.size === 0 &&
    this.hiddenNodes.size < SMALL_SELECTION_THRESHOLD &&
    !rootId &&
    !this.model.is2d();

    if (smallChange) {
      for (const node of this.hiddenNodes) {
        this.setVisibilityOnNode(node, visible, true, true, trackIsolation);
      }
      // Fire a single scene update event after processing all nodes.
      // Note that we can (and should) skip the bbox update, because the affected bboxes are already invalidated.
      this.viewerImpl.sceneUpdated(true, false, false, true);
    }

    this.isolatedNodes.clear();
    this.hiddenNodes.clear();

    if (smallChange) {
      return;
    }

    const root = rootId || (this.model ? this.model.getRootId() : null);
    if (root) {
      // 2D datasets may need to call setAllVisibility on the model. This can have two possible reasons:
      //  a) they may have no instance tree, so that setting visibility on root (as below) is not possible.
      //  b) even if they have an instance tree, setting visibility on root node will only reach selectable ids.
      //     2D datasets may also contain unselectable objects with id <=0. In this case, the call below
      //     is needed to hide/show these as well when using isolate/show-all.
      if (this.model.is2d()) {
        this.model.setAllVisibility(visible);
      }
      // if we have an instance tree, we call setVisible on the root node
      this.setVisibilityOnNode(root, visible, false, true, trackIsolation);
    } else {
      this.model.setAllVisibility(visible);
      this.updateNodeVisibilityTracking(SYNTHETIC_ROOT_ID, visible, trackIsolation);
      this.viewerImpl.sceneUpdated(true, false);
    }
  }

  /**
   * @param {number} dbId
   * @returns {boolean}
   */
  isNodeVisible(dbId) {
    const instanceTree = this.getInstanceTree();
    if (instanceTree) {
      // get visibility from instance tree
      return !instanceTree.isNodeHidden(dbId);
    } else {
      // If there is no instance tree, we have ids, but no hierarchy.
      // Therefore, an id is only hidden if it appears in hiddenNodes or
      // if there are isolated nodes and dbId is not among these.
      if (this.hiddenNodes.has(dbId)) return false;
      // it's not in hidden, but neither there is any isolation => use a fallback dummy root to check for "all visible"
      if (!this.isolatedNodes.size) return !this.hiddenNodes.has(SYNTHETIC_ROOT_ID);
      return this.isolatedNodes.has(dbId);
    }
  }

  isolate(node) {
    const nodes = Array.isArray(node) ? node : [node];
    const root = nodes.find((node) => this.#isRoot(node));
    if (!node) {
      // falsy node resets isolation
      this.setAllVisibility(true, false);
    } else if (root) {
      // explicit root forces isolation tracking
      this.setAllVisibility(true, true, root);
      // redundantly track the rest, as some intermediate nodes might be hidden,
      // but their children might still be visible (the "restore viewer state" case)
      const nodesWithoutRoot = nodes.filter((node) => !this.#isRoot(node));
      for (const notRoot of nodesWithoutRoot) {
        this.updateNodeVisibilityTracking(notRoot, true, true);
      }
    } else {
      this.isolateMultiple(nodes);
    }
  }

  isolateNone() {
    this.setAllVisibility(true);
  }

  //Makes the children of a given node visible and
  //everything else not visible
  isolateMultiple(nodeList) {

    //If given nodelist is null or is an empty array or contains the whole tree
    if (!nodeList || nodeList.length == 0) {
      this.setAllVisibility(true);
    } else
    {
      this.setAllVisibility(false);

      const lastIndex = nodeList.length - 1;
      for (let i = 0; i < nodeList.length; i++) {
        this.setVisibilityOnNode(nodeList[i], true, i !== lastIndex, true, true);
      }
    }
  }

  /**
   * Hides the given node and its children.
   *
   * @param {BubbleNode} node
   * @param {Boolean} fireEvent
   * @param {Boolean} skipIsolated
   * @returns
   */
  hide(node, fireEvent = true, skipIsolated = false) {
    const nodes = Array.isArray(node) ? node : [node];

    if (!nodes.length) return;

    const lastIndex = nodes.length - 1;
    for (let i = 0; i < nodes.length; ++i) {
      this.setVisibilityOnNode(nodes[i], false, i !== lastIndex, true, skipIsolated);
    }
    if (fireEvent) this.viewerImpl.api.dispatchEvent({ type: et.HIDE_EVENT, nodeIdArray: nodes, model: this.model });
  }

  show(node, fireEvent = true) {
    const nodes = Array.isArray(node) ? node : [node];

    if (!nodes.length) return;

    const lastIndex = nodes.length - 1;
    for (let i = 0; i < nodes.length; ++i) {
      this.setVisibilityOnNode(nodes[i], true, i !== lastIndex);
    }

    if (fireEvent) this.viewerImpl.api.dispatchEvent({ type: et.SHOW_EVENT, nodeIdArray: nodes, model: this.model });
  }

  toggleVisibility(node) {
    this.isNodeVisible(node) ? this.hide(node) : this.show(node);
  }

  /**
   * @param {number} node
   * @param {boolean} visible
   * @param {boolean} [skipEventing=false] - optional flag to skip the resulting event, defaults to false
   * @param {boolean} [setRecursive=true] - optional flag to set the visibility recursively, defaults to true
   * @param {boolean} [trackIsolation=false] - optional flag to track isolation, defaults to false
   */
  setVisibilityOnNode(node, visible, skipEventing = false, setRecursive = true, trackIsolation = false) {
    const fragmentList = this.model.getFragmentList();
    const instanceTree = this.getInstanceTree();

    // keep track on if isolation is already active
    const isolated = !!this.isolatedNodes.size;

    if (instanceTree) {
      const isRoot = this.#isRoot(node);

      // Fast path: bulk-set all node flags + fragment visibility at once,
      // avoiding the expensive per-node recursive traversal.
      if (isRoot && !instanceTree.hasLockedVisibility() && !this.model.is2d()) {
        instanceTree.setAllNodesHidden(!visible);
        this.model.setAllVisibility(visible);
      } else {
        //Recursively process the tree under the root (recursion is inclusive of the root)
        instanceTree.enumNodeChildren(node, (dbId) => {

          const visibleLocked = instanceTree.isNodeVisibleLocked(dbId);

          // if hiding the isolated node while the isolation tracking's been requested (the "restore viewer state" case),
          // or hiding the locked node, skip it and stop recursion into its children
          if (!visible && trackIsolation && this.isolatedNodes.has(dbId)) {
            return true;
          }

          instanceTree.setNodeHidden(dbId, !visible && !visibleLocked);

          if (this.model.is2d()) {
            // If dbId is otg, we need to map back to original dbIds, because the
            // vertex buffer contain original dbIds.
            const origDbId = this.model.reverseMapDbIdFor2D(dbId);

            fragmentList?.setObject2DGhosted(origDbId, !visible && !visibleLocked);
          } else {
            instanceTree.enumNodeFragments(dbId, (fragId) => {
              this.model.setVisibility(fragId, visible || visibleLocked);
            }, false);
          }

          this.#untrackChild(node, dbId);
        }, setRecursive);
      }
    } else {
      //No instance tree, assume fragId = dbId
      if (this.model.is2d()) {
        if (this.model.isLeaflet()) {
          this.model.setVisibility(null, visible);
        } else {
          fragmentList?.setObject2DGhosted(node, !visible);
        }
      } else {
        this.model.setVisibility(node, visible);
      }
    }

    this.updateNodeVisibilityTracking(node, visible, trackIsolation || isolated);

    // We use skipBboxUpdate=true, because model.setVisibility above already takes care of bbox invalidation.
    // The automatic bbox invalidation in sceneUpdated would unnecessarily trigger a full bbox recompute for all models - which can be much more than we actually need.
    this.viewerImpl.sceneUpdated(true, false, skipEventing, true);
  }

  /**
   * Updates a visibility tracking of the node
   *
   * A few moments to consider:
   * - an isolation means "show this node and hide the rest"
   * - the `trackIsolation` flag must be set by the callers of this, as it can't be derived otherwise
   * - the "all hidden" state is tracked by hiding the root node
   * - since initial state is "all shown", `hide/show` is tracked by using `hiddenNodes`, but
   * - when the state is "all hidden", `show` becomes "show some nodes, while hiding everything else" i.e. again the isolation, so that:
   *   - `hide` on an isolated node becomes "remove isolation"
   *   - after the last isolated node is removed, it must again become "all hidden"
   *   - `hide` on a non-isolated node simply means adding the node to the hidden
   *
   * Overall, it's all about to be able to store/restore the visibility state of the model w/o loss of information.
   *
   * @param {number} node - the dbId of the node to track
   * @param {boolean} visible - the visibility flag, which has been applied to the node
   * @param {boolean} trackIsolation - the flag indicating if isolation is to be tracked
   */
  updateNodeVisibilityTracking(node, visible, trackIsolation) {
    // for the root it's easy: clear everything and track the root
    if (this.#isRoot(node)) {
      this.hiddenNodes.clear();
      this.isolatedNodes.clear();
      if (visible) {
        if (trackIsolation) this.isolatedNodes.add(node);
      } else {
        this.hiddenNodes.add(node);
      }
      return;
    }
    if (visible) {
      // simple case: was hidden, now shown
      if (this.hiddenNodes.has(node)) {
        this.hiddenNodes.delete(node);
        if (trackIsolation) {
          this.isolatedNodes.add(node);
        }
      } else
      {
        // if everything was hidden (no isolation, and "all hidden" is marked with root),
        // start to track isolation even if it's not requested by the caller (see function description for details)
        if (this.isAllHidden()) {
          this.hiddenNodes.clear();
          this.isolatedNodes.add(node);
        } else {
          if (trackIsolation) {
            this.isolatedNodes.add(node);
          }
        }
      }
    } else
    {
      // something is isolated
      if (this.isolatedNodes.size) {
        if (this.isolatedNodes.has(node)) {
          this.isolatedNodes.delete(node);
          // now, since isolation was active, but there is no isolated nodes anymore:
          // - clear hidden nodes (no need to track them anymore)
          // - track "all hidden" with root
          if (!this.isolatedNodes.size) {
            this.hiddenNodes.clear();
            this.hiddenNodes.add(this.model.getRootId() || SYNTHETIC_ROOT_ID);
          }
        } else {
          // the non-isolated node to be hidden means we deal with a child of isolated node, simply track it
          this.hiddenNodes.add(node);
        }
      } else {
        // nothing was isolated, track iff it's not "all hidden" already
        if (!this.isAllHidden()) this.hiddenNodes.add(node);
      }
    }
  }

  isAllHidden() {
    return !this.isolatedNodes.size && this.hiddenNodes.has(this.model.getRootId() || SYNTHETIC_ROOT_ID);
  }

  toggleVisibleLocked(node) {
    const instanceTree = this.getInstanceTree();
    if (!instanceTree) return;
    this.lockNodeVisible(node, !instanceTree.isNodeVisibleLocked(node));
  }

  lockNodeVisible(nodeList, locked) {

    const instanceTree = this.getInstanceTree();

    if (!instanceTree) return;

    // keep track on if isolation is already active
    const isolated = !!this.isolatedNodes.size;

    const lockOneNode = (node) => {
      //Recursively process the tree under the root (recursion is inclusive of the root)
      instanceTree.enumNodeChildren(node, (dbId) => {
        instanceTree.lockNodeVisible(dbId, locked);
        if (locked) {
          instanceTree.setNodeHidden(dbId, false);

          if (this.model.is2d()) {
            // If dbId is otg, we need to map back to original dbIds, because the
            // vertex buffer contain original dbIds.
            const origDbId = this.model.reverseMapDbIdFor2D(dbId);

            this.model.getFragmentList().setObject2DGhosted(origDbId, false);
          } else {
            instanceTree.enumNodeFragments(dbId, (fragId) => {
              this.model.setVisibility(fragId, true);
            }, false);
          }

          this.#untrackChild(node, dbId);
        }
      }, true);

      if (locked) {
        this.updateNodeVisibilityTracking(node, locked, isolated);
        this.viewerImpl.sceneUpdated(true);
      }
    };

    if (Array.isArray(nodeList)) {
      nodeList.forEach((node) => lockOneNode(node));
    } else {
      lockOneNode(nodeList);
    }
  }

  isNodeVisibleLocked(node) {
    const instanceTree = this.getInstanceTree();
    return instanceTree?.isNodeVisibleLocked(node);
  }

  setNodeOff(node, isOff, nodeChildren, nodeFragments) {
    /**
     * This one is very special with regards to tracking of the isolated/hidden nodes.
     * It uses an independent flag - `NODE_FLAG_OFF` - and does not change the visibility flag, however,
     * the latter may still be changed by other methods, so that after the node is on again,
     * it appears visible or ghosted (or hidden, depending on the LMV settings) accordingly.
     * Also, the viewer state, as well as model browsers (LMV and ACC) do not support the off-nodes at the moment.
     * That been said, the off-nodes shall not be tracked using the existing `hidden/isolatedNodes` sets,
     * but a new set and its handling logic are to be added on demand, e.g., if somebody will report this as an issue.
     */

    const instanceTree = this.getInstanceTree();
    const fragmentList = this.model.getFragmentList();

    if (!instanceTree) {
      if (!fragmentList) return;

      if (this.model.is2d()) {
        fragmentList.setObject2DVisible(node, !isOff);
      } else {
        fragmentList.setFragOff(node, isOff);
      }
      this.viewerImpl.sceneUpdated(true);
      return;
    }

    if (nodeChildren && nodeFragments) {
      if (this.model.is2d()) {
        for (const dbId of nodeChildren) {
          instanceTree.setNodeOff(dbId, isOff);
          // If dbId is otg, we need to map back to original dbIds, because the
          // vertex buffer contain original dbIds.
          const origDbId = this.model.reverseMapDbIdFor2D(dbId);
          fragmentList?.setObject2DVisible(origDbId, !isOff);
        }
      } else {
        for (const dbId of nodeChildren) {
          instanceTree.setNodeOff(dbId, isOff);
        }
        for (const fragId of nodeFragments) {
          fragmentList?.setFragOff(fragId, isOff);
        }
      }
    } else {
      //Recursively process the tree under the root (recursion is inclusive of the root)
      instanceTree.enumNodeChildren(node, (dbId) => {
        instanceTree.setNodeOff(dbId, isOff);

        if (this.model.is2d()) {
          // If dbId is otg, we need to map back to original dbIds, because the
          // vertex buffer contain original dbIds.
          const origDbId = this.model.reverseMapDbIdFor2D(dbId);
          fragmentList?.setObject2DVisible(origDbId, !isOff);
        } else {
          instanceTree.enumNodeFragments(dbId, (fragId) => {
            fragmentList?.setFragOff(fragId, isOff);
          }, false);
        }
      }, true);
    }

    this.viewerImpl.sceneUpdated(true);
  }

  /**
   * Returns the visibility that most objects in the model currently have.
   * Reference: https://git.autodesk.com/BIM360/visualization-schemas/blob/cd1b911426a82fd3d5ccd2134ff26b41e5d34049/validator/autodesk.viewer_state-3.0.0%23valid2.json#L79
   *
   * @returns {Boolean} - visibility of most nodes in the model.
   */
  getDefaultVisibility() {
    if (this.isolatedNodes.size === 0 && this.hiddenNodes.size === 0) {
      return true;
    }

    return this.isolatedNodes.size === 0;
  }

  /**
   * Returns the visibility for each object (dbId) in the model.
   * Order by hierarchy, i.e., parent is listed before its children, is guaranteed.
   * Reference: https://git.autodesk.com/BIM360/visualization-schemas/blob/cd1b911426a82fd3d5ccd2134ff26b41e5d34049/validator/autodesk.viewer_state-3.0.0%23valid2.json#L79
   *
   * @returns { Array< Array<Number, Boolean> >} - Array of pairs of dbIds with their visibility (true or false)
   */
  getVisibility() {
    const visibilityOverrides = [];
    const added = new Set();

    this.isolatedNodes.forEach((dbId) => {visibilityOverrides.push([dbId, true]);added.add(dbId);});
    this.hiddenNodes.forEach((dbId) => {visibilityOverrides.push([dbId, false]);added.add(dbId);});

    const instanceTree = this.getInstanceTree();
    const root = this.model?.getRootId();
    if (!instanceTree || !root) {
      this.#sortByHierarchy(visibilityOverrides);
      return visibilityOverrides;
    }

    const isParentHidden = (nodeId) => {
      const parentId = instanceTree.getNodeParentId(nodeId);
      return this.isNodeVisible(parentId) === false;
    };

    // Find visible child nodes below hidden nodes
    this.hiddenNodes.forEach((nodeId) => {
      instanceTree.enumNodeChildren(nodeId, (childNodeId) => {
        const isNodeVisible = this.isNodeVisible(childNodeId);
        if (isNodeVisible && isParentHidden(childNodeId) && !added.has(childNodeId)) {
          visibilityOverrides.push([childNodeId, true]);
          added.add(childNodeId);
        }
      }, true);
    });

    added.clear();

    this.#sortByHierarchy(visibilityOverrides);
    return visibilityOverrides;
  }

  /**
   * Sets the visibility per object given by 'visibility'.
   *
   * @param {Boolean} defaultVisibility - if not otherwise specified, object's visibility is set to this value.
   * @param {Array< <Number, Boolean> >} visibilityOverrides - map of dbIds to their visibility (true or false)
   * @param {Boolean} overridesOrderedByHierarchy - True if overrides are sorted by object hierarchy, i.e.,
   *                                                children always occur after their parents. False otherwise.
   */
  setVisibility(defaultVisibility, visibilityOverrides, overridesOrderedByHierarchy) {
    // trivial case
    if (visibilityOverrides.length === 0) {
      this.setAllVisibility(defaultVisibility);
      return;
    }

    if (!overridesOrderedByHierarchy) {
      this.#sortByHierarchy(visibilityOverrides);
    }

    this.setAllVisibility(defaultVisibility);

    const showNodes = [];
    const hideNodes = [];
    visibilityOverrides.forEach((entry) => {
      const dbId = Number(entry[0]);
      const visible = entry[1];
      if (visible) {
        showNodes.push(dbId);
        this.show(dbId, false);
      } else {
        hideNodes.push(dbId);
        this.hide(dbId, false);
      }
    });
    this.viewerImpl.api.dispatchEvent({ type: et.SHOW_EVENT, nodeIdArray: showNodes, model: this.model });
    this.viewerImpl.api.dispatchEvent({ type: et.HIDE_EVENT, nodeIdArray: hideNodes, model: this.model });

    this.viewerImpl.api.dispatchEvent({ type: et.AGGREGATE_ISOLATION_CHANGED_EVENT, isolation: [{ model: this.model, ids: this.getIsolatedNodes() }] });
    this.viewerImpl.api.dispatchEvent({ type: et.AGGREGATE_HIDDEN_CHANGED_EVENT, hidden: [{ model: this.model, ids: this.getHiddenNodes() }] });

    this.viewerImpl.sceneUpdated(true, false, false);
  }

  /**
   * Does in-place sorting to a given list of visibility overrides by node hierarchy, i.e.,
   * parent nodes always occur before their children.
   *
   * @param {Array<Array<Number, Boolean>} overrides - the visibility overrides to sort
   */
  #sortByHierarchy(overrides) {
    const instanceTree = this.getInstanceTree();
    if (!instanceTree) {
      return;
    }
    overrides.sort((a, b) => {
      const dbId1 = Number(a[0]);
      const dbId2 = Number(b[0]);
      return instanceTree.getNodeLevel(dbId1) - instanceTree.getNodeLevel(dbId2);
    });
  }

  /**
   * @param {number | object} node - the node to check
   * @returns {boolean} - true if the node is the root node, false otherwise
   */
  #isRoot(node) {
    const rootId = this.model.getRootId() || SYNTHETIC_ROOT_ID;
    if (typeof node === 'number') return node === rootId;
    if (typeof node == 'object') return node?.dbId === rootId;
    // Should actually never happen if node has expected type.
    return false;
  }

  #untrackChild(parentId, childId) {
    if (parentId === childId) return;
    this.isolatedNodes.delete(childId);
    this.hiddenNodes.delete(childId);
  }
}